import { useRef } from 'react'
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect'

/**
 * Split-text reveal — CSS-first (Web Animations API + IntersectionObserver),
 * no GSAP. Public API unchanged. The text is split into per-word/char spans via
 * DOM APIs (never innerHTML, so CMS-entered headlines stay literal text), an
 * aria-label preserves the accessible name, and the inner spans animate up/in.
 */

const EXPO_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)'

function cssEasing(ease?: string): string {
  if (!ease || ease === 'expo.out') return EXPO_OUT
  if (ease === 'none') return 'linear'
  return ease
}

interface TextRevealOptions {
  type?: 'words' | 'chars'
  duration?: number
  y?: number
  stagger?: number
  delay?: number
  ease?: string
  scrollTrigger?: boolean
  /** Accepted for API compatibility (old ScrollTrigger start); no longer used. */
  start?: string
  enabled?: boolean
}

function splitIntoSpans(element: HTMLElement, type: 'words' | 'chars'): HTMLSpanElement[] {
  const originalText = element.textContent || ''
  element.textContent = ''
  const inners: HTMLSpanElement[] = []

  const makeSpan = (text: string, outerClass: string) => {
    const outer = document.createElement('span')
    outer.className = outerClass
    outer.style.display = 'inline-block'
    outer.style.overflow = 'hidden'
    outer.setAttribute('aria-hidden', 'true')
    const inner = document.createElement('span')
    inner.className = 'text-reveal-inner'
    inner.style.display = 'inline-block'
    inner.textContent = text
    outer.appendChild(inner)
    inners.push(inner)
    return outer
  }

  if (type === 'words') {
    const words = originalText.split(/\s+/).filter(Boolean)
    words.forEach((word, i) => {
      if (i > 0) element.appendChild(document.createTextNode(' '))
      element.appendChild(makeSpan(word, 'text-reveal-word'))
    })
  } else {
    for (const char of originalText.split('')) {
      if (char === ' ') {
        element.appendChild(document.createTextNode(' '))
      } else {
        element.appendChild(makeSpan(char, 'text-reveal-char'))
      }
    }
  }

  element.setAttribute('aria-label', originalText)
  return inners
}

const prefersReducedMotion = () =>
  typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches

interface RevealRun {
  y: number
  blur?: number
  durationSec: number
  staggerSec: number
  delaySec: number
  easing: string
  onScroll: boolean
}

function animateSpans(host: HTMLElement, spans: HTMLElement[], r: RevealRun): () => void {
  const fromTransform = `translateY(${r.y}px)`
  for (const s of spans) {
    s.style.opacity = '0'
    s.style.transform = fromTransform
    if (r.blur) s.style.filter = `blur(${r.blur}px)`
    s.style.willChange = 'opacity, transform'
  }

  const from: Keyframe = { opacity: 0, transform: fromTransform, ...(r.blur ? { filter: `blur(${r.blur}px)` } : {}) }
  const to: Keyframe = { opacity: 1, transform: 'translateY(0px)', ...(r.blur ? { filter: 'blur(0px)' } : {}) }

  const anims: Animation[] = []
  const run = () => {
    spans.forEach((s, i) => {
      anims.push(
        s.animate([from, to], {
          duration: r.durationSec * 1000,
          easing: r.easing,
          delay: (r.delaySec + r.staggerSec * i) * 1000,
          fill: 'both',
        })
      )
    })
  }

  let io: IntersectionObserver | null = null
  if (!r.onScroll || host.getBoundingClientRect().top < (window.innerHeight || 0) * 0.98) {
    run()
  } else {
    io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            io?.disconnect()
            io = null
            run()
          }
        }
      },
      { rootMargin: '0px 0px -2% 0px' }
    )
    io.observe(host)
  }

  return () => {
    io?.disconnect()
    anims.forEach((a) => a.cancel())
  }
}

export function useTextReveal<T extends HTMLElement>(options: TextRevealOptions = {}) {
  const elementRef = useRef<T>(null)
  const {
    type = 'words',
    duration = 0.5,
    y = 20,
    stagger = 0.03,
    delay = 0,
    ease = 'expo.out',
    scrollTrigger: useScrollTrigger = false,
    enabled = true,
  } = options

  useIsomorphicLayoutEffect(() => {
    if (!enabled || prefersReducedMotion() || !elementRef.current) return
    const element = elementRef.current
    const originalHTML = element.innerHTML
    const spans = splitIntoSpans(element, type)
    if (!spans.length) return
    const cleanupAnim = animateSpans(element, spans, {
      y,
      durationSec: duration,
      staggerSec: stagger,
      delaySec: delay,
      easing: cssEasing(ease),
      onScroll: useScrollTrigger,
    })
    return () => {
      cleanupAnim()
      element.innerHTML = originalHTML
      element.removeAttribute('aria-label')
    }
  }, [type, duration, y, stagger, delay, ease, useScrollTrigger, enabled])

  return elementRef
}

export function useLuxeTextReveal<T extends HTMLElement>(options: TextRevealOptions & { blur?: number } = {}) {
  const elementRef = useRef<T>(null)
  const {
    type = 'words',
    duration = 0.55,
    y = 24,
    blur = 3,
    stagger = 0.04,
    delay = 0,
    ease = 'expo.out',
    scrollTrigger: useScrollTrigger = false,
    enabled = true,
  } = options

  useIsomorphicLayoutEffect(() => {
    if (!enabled || prefersReducedMotion() || !elementRef.current) return
    const element = elementRef.current
    const originalHTML = element.innerHTML
    const spans = splitIntoSpans(element, type)
    if (!spans.length) return
    const cleanupAnim = animateSpans(element, spans, {
      y,
      blur,
      durationSec: duration,
      staggerSec: stagger,
      delaySec: delay,
      easing: cssEasing(ease),
      onScroll: useScrollTrigger,
    })
    return () => {
      cleanupAnim()
      element.innerHTML = originalHTML
      element.removeAttribute('aria-label')
    }
  }, [type, duration, y, blur, stagger, delay, ease, useScrollTrigger, enabled])

  return elementRef
}

export default useTextReveal
